import { useMemo, useState } from 'react';
import { useFormacao } from '@/context/FormacaoContext';
import { useFuncionariosFormacao } from '@/context/FuncionariosFormacaoContext';
import { estadoNormalizado } from '@/utils/estadoUtils';

function formatEuro(v: number) {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);
}

interface FormacaoPorCarreira {
  formacao_id: string;
  nome: string;
  entidade_formadora: string | null;
  categoria: string | null;
  modalidade: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  duracao_horas: number;
  estado: string;
  participantes: number;
  certificados: number;
  custo: number;
}

interface CarreiraRow {
  carreira: string;
  totalParticipantes: number;
  totalHoras: number;
  totalCusto: number;
  totalCertificados: number;
  totalFormacoes: number;
  formacoes: FormacaoPorCarreira[];
}

export default function RelatorioPorCarreiraFormacao() {
  const { formacoes, participantes, loading } = useFormacao();
  const { funcionarios } = useFuncionariosFormacao();

  const [anoSelecionado, setAnoSelecionado] = useState('2025');
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const anosDisponiveis = useMemo(() => {
    const anos = new Set<string>();
    formacoes.forEach((f) => {
      const a = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      if (a) anos.add(a);
    });
    const lista = Array.from(anos).sort((a, b) => b.localeCompare(a));
    if (lista.length === 0) lista.push('2025');
    return lista;
  }, [formacoes]);

  const dadosPorCarreira = useMemo((): CarreiraRow[] => {
    const formacoesAno = formacoes.filter((f) => {
      const ano = (f.data_inicio ?? f.data_fim ?? '').slice(0, 4);
      return ano === anoSelecionado;
    });

    const idsFormacoesAno = new Set(formacoesAno.map((f) => f.id));
    const participantesAno = participantes.filter((p) => idsFormacoesAno.has(p.formacao_id));

    const carreiraMap: Record<string, Record<string, FormacaoPorCarreira>> = {};

    participantesAno.forEach((p) => {
      const func = funcionarios.find((fu) => fu.id === p.funcionario_id);
      if (!func) return;

      const carreira = func.carreira ?? 'Outra';
      const formacao = formacoesAno.find((f) => f.id === p.formacao_id);
      if (!formacao) return;

      if (!carreiraMap[carreira]) carreiraMap[carreira] = {};

      if (!carreiraMap[carreira][formacao.id]) {
        carreiraMap[carreira][formacao.id] = {
          formacao_id: formacao.id,
          nome: formacao.nome,
          entidade_formadora: formacao.entidade_formadora,
          categoria: formacao.categoria,
          modalidade: formacao.modalidade,
          data_inicio: formacao.data_inicio,
          data_fim: formacao.data_fim,
          duracao_horas: Number(formacao.duracao_horas) || 0,
          estado: formacao.estado ?? 'planeada',
          participantes: 0,
          certificados: 0,
          custo: 0,
        };
      }

      carreiraMap[carreira][formacao.id].participantes += 1;
      if (p.certificado_emitido) {
        carreiraMap[carreira][formacao.id].certificados += 1;
      }
      carreiraMap[carreira][formacao.id].custo += Number(p.custo || 0);
    });

    const rows: CarreiraRow[] = Object.entries(carreiraMap).map(([carreira, formacoesMap]) => {
      const formacoesList = Object.values(formacoesMap).sort((a, b) => {
        const da = a.data_inicio ?? '';
        const db = b.data_inicio ?? '';
        return db.localeCompare(da);
      });

      const totalParticipantes = formacoesList.reduce((s, f) => s + f.participantes, 0);
      // Horas = soma das durações das formações únicas (não multiplicado por participantes)
      const totalHoras = formacoesList.reduce((s, f) => s + f.duracao_horas, 0);
      const totalCusto = formacoesList.reduce((s, f) => s + f.custo, 0);
      const totalCertificados = formacoesList.reduce((s, f) => s + f.certificados, 0);

      return {
        carreira,
        totalParticipantes,
        totalHoras,
        totalCusto,
        totalCertificados,
        totalFormacoes: formacoesList.length,
        formacoes: formacoesList,
      };
    });

    rows.sort((a, b) => b.totalParticipantes - a.totalParticipantes);

    if (search.trim()) {
      const q = search.toLowerCase();
      return rows.filter((r) =>
        r.carreira.toLowerCase().includes(q) ||
        r.formacoes.some((f) => f.nome.toLowerCase().includes(q) || (f.entidade_formadora ?? '').toLowerCase().includes(q))
      );
    }

    return rows;
  }, [formacoes, participantes, funcionarios, anoSelecionado, search]);

  const totais = useMemo(() => {
    return dadosPorCarreira.reduce(
      (acc, r) => ({
        formacoes: acc.formacoes + r.totalFormacoes,
        participantes: acc.participantes + r.totalParticipantes,
        horas: acc.horas + r.totalHoras,
        custo: acc.custo + r.totalCusto,
        certificados: acc.certificados + r.totalCertificados,
      }),
      { formacoes: 0, participantes: 0, horas: 0, custo: 0, certificados: 0 }
    );
  }, [dadosPorCarreira]);

  function toggleCarreira(carreira: string) {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(carreira)) next.delete(carreira);
      else next.add(carreira);
      return next;
    });
  }

  function expandirTodas() {
    setExpandidas(new Set(dadosPorCarreira.map((r) => r.carreira)));
  }

  function recolherTodas() {
    setExpandidas(new Set());
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-2xl animate-spin text-zinc-300" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Filtros */}
      <div className="bg-white rounded-xl border border-zinc-100 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
            <i className="ri-calendar-line text-sm" />
          </div>
          <span className="text-[12px] font-semibold text-zinc-600">Ano</span>
        </div>
        <div className="flex items-center gap-1.5">
          {anosDisponiveis.map((a) => (
            <button
              key={a}
              onClick={() => setAnoSelecionado(a)}
              className={`whitespace-nowrap px-4 py-2 text-[13px] font-semibold rounded-lg border-2 transition-all cursor-pointer ${
                anoSelecionado === a
                  ? 'border-zinc-900 bg-zinc-900 text-white'
                  : 'border-zinc-100 text-zinc-500 bg-white hover:border-zinc-300'
              }`}
            >
              {a}
            </button>
          ))}
        </div>

        <div className="h-6 w-px bg-zinc-200 mx-1" />

        <div className="relative flex-1 min-w-[200px]">
          <div className="w-8 h-8 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="Pesquisar carreira, formação ou entidade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={expandirTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Expandir tudo
          </button>
          <button
            onClick={recolherTodas}
            className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 px-3 py-2 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            Recolher tudo
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { icon: 'ri-briefcase-line', value: String(dadosPorCarreira.length), label: 'Carreiras' },
          { icon: 'ri-book-open-line', value: String(totais.formacoes), label: 'Formações' },
          { icon: 'ri-group-line', value: String(totais.participantes), label: 'Participações' },
          { icon: 'ri-time-line', value: `${totais.horas}h`, label: 'Horas Totais' },
          { icon: 'ri-money-euro-circle-line', value: formatEuro(totais.custo), label: 'Custo Total' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-zinc-100 px-4 py-4 text-center">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 mx-auto mb-2">
              <i className={`${kpi.icon} text-sm text-zinc-500`} />
            </div>
            <p className="text-[22px] font-black text-zinc-900 leading-none">{kpi.value}</p>
            <p className="text-[9px] font-bold text-zinc-400 uppercase tracking-widest mt-1.5">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Tabela por Carreira */}
      <div className="bg-white rounded-xl border border-zinc-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-zinc-900 rounded-full" />
            <h3 className="text-[14px] font-bold text-zinc-800">Formações por Carreira — {anoSelecionado}</h3>
          </div>
          <p className="text-[11px] text-zinc-400">
            {dadosPorCarreira.length} carreira(s) · Clique para expandir detalhes
          </p>
        </div>

        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-6 py-3 text-left text-[10px] font-bold text-zinc-400 uppercase tracking-wider w-[240px]">Carreira</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Formações</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Participantes</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Horas</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Certificados</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Taxa Cert.</th>
              <th className="px-4 py-3 text-center text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Custo Total</th>
            </tr>
          </thead>
          <tbody>
            {dadosPorCarreira.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-[13px] text-zinc-400">
                  <div className="flex flex-col items-center gap-2">
                    <i className="ri-search-line text-3xl text-zinc-200" />
                    <p>Nenhuma formação encontrada para {anoSelecionado}</p>
                  </div>
                </td>
              </tr>
            ) : (
              dadosPorCarreira.map((row) => {
                const taxaCert = row.totalParticipantes > 0
                  ? Math.round((row.totalCertificados / row.totalParticipantes) * 100)
                  : 0;
                return (
                  <>
                    {/* Linha principal */}
                    <tr
                      key={`cat-${row.carreira}`}
                      onClick={() => toggleCarreira(row.carreira)}
                      className="border-b border-zinc-100 bg-zinc-50/40 hover:bg-zinc-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center text-zinc-400 flex-shrink-0">
                            {expandidas.has(row.carreira) ? (
                              <i className="ri-arrow-down-s-line text-sm" />
                            ) : (
                              <i className="ri-arrow-right-s-line text-sm" />
                            )}
                          </div>
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-100 flex-shrink-0">
                            <span className="text-[11px] font-black text-zinc-500">{row.carreira.charAt(0)}</span>
                          </div>
                          <span className="text-[13px] font-bold text-zinc-800">{row.carreira}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-sky-100 text-sky-700 text-[12px] font-bold">
                          {row.totalFormacoes}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-violet-100 text-violet-700 text-[12px] font-bold">
                          {row.totalParticipantes}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-amber-100 text-amber-700 text-[12px] font-bold">
                          {row.totalHoras}h
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[12px] font-bold text-emerald-600">{row.totalCertificados}</span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="flex-1 max-w-[60px] h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                taxaCert === 100 ? 'bg-emerald-500' : taxaCert >= 80 ? 'bg-amber-400' : 'bg-rose-400'
                              }`}
                              style={{ width: `${taxaCert}%` }}
                            />
                          </div>
                          <span className="text-[11px] font-bold text-zinc-600 w-8 text-right flex-shrink-0">{taxaCert}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-[12px] font-semibold text-zinc-700">{formatEuro(row.totalCusto)}</span>
                      </td>
                    </tr>

                    {/* Sub-linhas */}
                    {expandidas.has(row.carreira) &&
                      row.formacoes.map((f) => {
                        const taxaFCert = f.participantes > 0
                          ? Math.round((f.certificados / f.participantes) * 100)
                          : 0;
                        return (
                          <tr key={`${row.carreira}-${f.formacao_id}`} className="border-b border-zinc-50 bg-white">
                            <td className="pl-14 pr-4 py-2.5">
                              <div className="flex items-start gap-2">
                                <div className="w-5 h-5 flex items-center justify-center text-zinc-300 flex-shrink-0 mt-0.5">
                                  <i className="ri-corner-down-right-line text-xs" />
                                </div>
                                <div>
                                  <p className="text-[12px] font-semibold text-zinc-800">{f.nome}</p>
                                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                    {f.categoria && (
                                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500">
                                        {f.categoria}
                                      </span>
                                    )}
                                    <span className="text-[9px] text-zinc-400">{f.entidade_formadora ?? '—'}</span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] text-zinc-400">
                                      {f.data_inicio
                                        ? new Date(f.data_inicio).toLocaleDateString('pt-PT', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })
                                        : '—'}
                                      {f.data_fim && f.data_fim !== f.data_inicio
                                        ? ` → ${new Date(f.data_fim).toLocaleDateString('pt-PT', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric',
                                          })}`
                                        : ''}
                                    </span>
                                    <span
                                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
                                        estadoNormalizado(f.estado) === 'concluida'
                                          ? 'bg-emerald-50 text-emerald-700'
                                          : 'bg-zinc-100 text-zinc-500'
                                      }`}
                                    >
                                      {estadoNormalizado(f.estado) === 'concluida' ? 'Concluída' : 'Planeada'}
                                    </span>
                                    {f.modalidade && (
                                      <span className="text-[9px] text-zinc-400">{f.modalidade}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-[11px] text-zinc-500">{f.duracao_horas}h</span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-violet-50 text-violet-600 text-[11px] font-bold">
                                {f.participantes}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="inline-flex items-center justify-center min-w-[40px] h-6 px-2 rounded-full bg-amber-50 text-amber-600 text-[11px] font-bold">
                                {f.duracao_horas}h
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-[11px] font-bold text-emerald-600">{f.certificados}</span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <div className="flex-1 max-w-[50px] h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      taxaFCert === 100
                                        ? 'bg-emerald-500'
                                        : taxaFCert >= 80
                                        ? 'bg-amber-400'
                                        : 'bg-rose-400'
                                    }`}
                                    style={{ width: `${taxaFCert}%` }}
                                  />
                                </div>
                                <span className="text-[10px] font-bold text-zinc-600 w-7 text-right flex-shrink-0">{taxaFCert}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-[11px] text-zinc-600">{formatEuro(f.custo)}</span>
                            </td>
                          </tr>
                        );
                      })}
                  </>
                );
              })
            )}

            {/* TOTAL GERAL */}
            {dadosPorCarreira.length > 0 && (
              <tr className="bg-zinc-900 border-t-2 border-zinc-900">
                <td className="px-6 py-4">
                  <span className="text-[13px] font-black text-white tracking-wide">TOTAL GERAL</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.formacoes}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[32px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.participantes}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="inline-flex items-center justify-center min-w-[40px] h-7 px-2.5 rounded-full bg-white/20 text-white text-[12px] font-black">
                    {totais.horas}h
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[12px] font-black text-emerald-400">{totais.certificados}</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[12px] font-black text-white">
                    {totais.participantes > 0
                      ? `${Math.round((totais.certificados / totais.participantes) * 100)}%`
                      : '—'}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[13px] font-black text-white">{formatEuro(totais.custo)}</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-zinc-400 text-center">
        Horas = duração total das formações com participantes de cada carreira. Custo = soma dos custos individuais de cada participante.
      </p>
    </div>
  );
}